import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import { toServerDateTimeNoTimezone } from '@/globals';
import Validatable from '@/validator/Validatable';
import i18n from '@/vueI18n';

import AdvancedReportExecution from './AdvancedReportExecution';

interface IAdvancedReportSchedulePreviewAPI {
  reportschedule_id: number;
  execution_timezone: string;
  first_event: string;
  last_event: string;
  recurring_period: string;
  day_of_month: string;
}

interface IAdvancedReportScheduleAPI {
  customreportschedule_id: number;
  report_type: string;
  report_parameters: any;
  execution_timezone: string;
  first_local_execution_time: string;
  last_local_execution_time: string;
  recurring_period: string;
  day_of_month: string;
  notification_emails: string;
}

interface IAdvancedReportSchedule {
  id: number;
  reportType: string;
  reportParameters: any;
  executionTimezone: string;
  firstLocalExecutionTime: DateTime;
  lastLocalExecutionTime: DateTime;
  recurringPeriod: string;
  dayOfMonth: string;
  notificationEmails: string[];
}

export default class AdvancedReportSchedule extends Validatable implements IAdvancedReportSchedule {
  reportType: string;
  reportParameters: any;
  executionTimezone: string;
  firstLocalExecutionTime: DateTime;
  lastLocalExecutionTime: DateTime;
  recurringPeriod: string;
  dayOfMonth: string;
  notificationEmails: string[];

  constructor(
    id = -1,
    reportType = null,
    reportParameters = null,
    executionTimezone = null,
    firstLocalExecutionTime = null,
    lastLocalExecutionTime = null,
    recurringPeriod = null,
    dayOfMonth = null,
    notificationEmails = [],
  ) {
    super();
    this.id = id;
    this.reportType = reportType;
    this.reportParameters = reportParameters;
    this.executionTimezone = executionTimezone;
    this.firstLocalExecutionTime = firstLocalExecutionTime;
    this.lastLocalExecutionTime = lastLocalExecutionTime;
    this.recurringPeriod = recurringPeriod;
    this.dayOfMonth = dayOfMonth;
    this.notificationEmails = notificationEmails;
  }

  get formattedType() {
    const type = AdvancedReportExecution.getTypes().find((x) => x.id === this.reportType);
    return !type ? capitalize(i18n.global.t('names.unknown', { n: 1 })) : type.name;
  }

  get definition() {
    return {
      timezone: this.timezone,
      breakdown: this.breakdown.toUpperCase(),
      dates: this.dates,
    };
  }

  get breakdown() {
    return this.reportParameters.breakdown;
  }

  get timezone() {
    return this.reportParameters.timezone;
  }

  get dates() {
    return this.reportParameters.report_dates;
  }

  get description() {
    let desc = '';
    switch (this.recurringPeriod) {
      case null: {
        desc = i18n.global.t('messages.reportScheduleDescriptionOneTime', [
          this.firstLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
        ]);
        break;
      }
      case 'DAILY': {
        if (this.lastLocalExecutionTime == null) {
          desc = i18n.global.t('messages.reportScheduleDescriptionDaily', [
            this.firstLocalExecutionTime.toLocaleString(DateTime.TIME_SIMPLE),
            this.firstLocalExecutionTime.toLocaleString(DateTime.DATE_FULL),
          ]);
        } else {
          desc = i18n.global.t('messages.reportScheduleDescriptionDailyUntil', [
            this.firstLocalExecutionTime.toLocaleString(DateTime.TIME_SIMPLE),
            this.firstLocalExecutionTime.toLocaleString(DateTime.DATE_FULL),
            this.lastLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
          ]);
        }
        break;
      }
      case 'WEEKLY': {
        if (this.lastLocalExecutionTime == null) {
          desc = i18n.global.t('messages.reportScheduleDescriptionWeekly', [
            this.firstLocalExecutionTime.toLocaleString(DateTime.TIME_SIMPLE),
            this.firstLocalExecutionTime.toLocaleString(DateTime.DATE_FULL),
          ]);
        } else {
          desc = i18n.global.t('messages.reportScheduleDescriptionWeeklyUntil', [
            this.firstLocalExecutionTime.toLocaleString(DateTime.TIME_SIMPLE),
            this.firstLocalExecutionTime.toLocaleString(DateTime.DATE_FULL),
            this.lastLocalExecutionTime.toLocaleString(DateTime.TIME_SIMPLE),
            this.lastLocalExecutionTime.toLocaleString(DateTime.DATE_FULL),
          ]);
        }
        break;
      }
      case 'MONTHLY': {
        switch (this.dayOfMonth) {
          case 'FIRST': {
            if (this.lastLocalExecutionTime == null) {
              desc = i18n.global.t('messages.reportScheduleDescriptionMonthlyOnFirstDay', [
                this.firstLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
              ]);
            } else {
              desc = i18n.global.t('messages.reportScheduleDescriptionMonthOnFirstDayUntil', [
                this.firstLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
                this.lastLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
              ]);
            }
            break;
          }
          case 'LAST': {
            if (this.lastLocalExecutionTime == null) {
              desc = i18n.global.t('messages.reportScheduleDescriptionMonthlyOnLastDay', [
                this.firstLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
              ]);
            } else {
              desc = i18n.global.t('messages.reportScheduleDescriptionMonthOnLastDayUntil', [
                this.firstLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
                this.lastLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
              ]);
            }
            break;
          }
          default: {
            if (this.lastLocalExecutionTime == null) {
              desc = i18n.global.t('messages.reportScheduleDescriptionMonthly', [
                this.firstLocalExecutionTime.day,
                this.firstLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
              ]);
            } else {
              desc = i18n.global.t('messages.reportScheduleDescriptionMonthUntil', [
                this.firstLocalExecutionTime.day,
                this.firstLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
                this.lastLocalExecutionTime.toLocaleString(DateTime.DATETIME_MED),
              ]);
            }
            break;
          }
        }
        break;
      }
      default:
        break;
    }
    return capitalize(desc);
  }

  static fromApi(apiObject: IAdvancedReportScheduleAPI) {
    return new AdvancedReportSchedule(
      apiObject.customreportschedule_id,
      apiObject.report_type,
      apiObject.report_parameters,
      apiObject.execution_timezone,
      DateTime.fromISO(apiObject.first_local_execution_time),
      apiObject.last_local_execution_time != null ? DateTime.fromISO(apiObject.last_local_execution_time) : null,
      apiObject.recurring_period,
      apiObject.day_of_month,
      apiObject.notification_emails?.split(','),
    );
  }

  static fromObject(obj: IAdvancedReportSchedule) {
    return super.fromObject(obj, new AdvancedReportSchedule());
  }

  static validatorCreateRules() {
    return {
      executionTimezone: 'required',
      firstEvent: 'required',
    };
  }

  toApi(): IAdvancedReportScheduleAPI {
    return {
      customreportschedule_id: this.id,
      report_type: this.reportType,
      report_parameters: this.reportParameters,
      execution_timezone: this.executionTimezone,
      first_local_execution_time: toServerDateTimeNoTimezone(this.firstLocalExecutionTime),
      last_local_execution_time:
        this.lastLocalExecutionTime != null ? toServerDateTimeNoTimezone(this.lastLocalExecutionTime) : null,
      recurring_period: this.recurringPeriod,
      day_of_month: this.dayOfMonth,
      notification_emails: this.notificationEmails.toString(),
    };
  }

  toPreviewApi(): IAdvancedReportSchedulePreviewAPI {
    return {
      reportschedule_id: this.id,
      execution_timezone: this.executionTimezone,
      first_event: toServerDateTimeNoTimezone(this.firstLocalExecutionTime),
      last_event: this.lastLocalExecutionTime != null ? toServerDateTimeNoTimezone(this.lastLocalExecutionTime) : null,
      recurring_period: this.recurringPeriod,
      day_of_month: this.dayOfMonth,
    };
  }
}
